import { ReactNode, Suspense, createContext, lazy, useContext, useEffect, useState } from 'react'
import { HashRouter, Navigate, Route, Routes, useLocation, useNavigate, useParams } from 'react-router-dom'

import { ActionPerformed, PushNotifications } from '@capacitor/push-notifications'
import { Capacitor } from '@capacitor/core'
import { useColorMode } from '@chakra-ui/react'
import AppUrlListener from './AppUrlListener.tsx'
import ForceUpdateModal from './components/modals/ForceUpdateModal'
import AdminWrapper from './components/wrappers/AdminWrapper'
import AuthWrapper from './components/wrappers/AuthWrapper'
import UserWrapper from './components/wrappers/UserWrapper'
import useAuthentication from './hooks/useAuth'
import IndexPage from './pages/Index'
import LoadingPage from './pages/Loading'

//import { useMutation } from 'react-query'

//import { /*patchRegistration,*/ putLocation } from './api/user/home/home'

import { setBackForwardNavigationGestures } from 'capacitor-plugin-ios-webview-configurator'
import { triggerHealthKitSync } from './hooks/useHealthKitSync'
import { SwipeBack, isIOS } from './native/SwipeBack'

//import { getMessaging, getToken } from "firebase/messaging";

const LoginPage = lazy(() => import('./pages/auth/Login'))
const RegisterPage = lazy(() => import('./pages/auth/Register'))
const PasswordResetPage = lazy(() => import('./pages/auth/PasswordReset'))

const EventCheckout = lazy(() => import('./pages/checkout/EventCheckout.tsx'))
const BookCheckout = lazy(() => import('./pages/checkout/BookCheckout.tsx'))
const PackCheckout = lazy(() => import('./pages/checkout/PackCheckout.tsx'))
const EventSuccess = lazy(() => import('./pages/checkout/EventSuccess.tsx'))
const BookSuccess = lazy(() => import('./pages/checkout/BookSuccess.tsx'))

const UserHomePage = lazy(() => import('./pages/user/Home'))
const UserPlayPage = lazy(() => import('./pages/user/Play'))
const UserPlayExpandPage = lazy(() => import('./pages/user/PlayExpand'))
const VenueLayout = lazy(() => import('./pages/user/venuePlay/VenueLayout'))
const VenuePlayEventsPage = lazy(() => import('./pages/user/venuePlay/VenuePlayEvents'))
const UserPlayEventPage = lazy(() => import('./pages/user/PlayEvent'))
const UserEventSharePage = lazy(() => import('./pages/user/EventShare'))
const EventRedirect = lazy(() => import('./pages/user/EventRedirect'))

//const UserPlayCheckoutPage = lazy(() => import ('./pages/user/PlayCheckout'))
//const UserPlayCheckoutSuccessPage = lazy(() => import ('./pages/user/PlayCheckoutSuccess'))
//const UserPlayCheckoutErrorPage = lazy(() => import ('./pages/user/PlayCheckoutError'))
const UserCommunityPage = lazy(() => import('./pages/user/Community'))
const UserMessagesPage = lazy(() => import('./pages/user/Messages'))
const UserProShopPage = lazy(() => import('./pages/user/ProShop'))
const UserProfilePage = lazy(() => import('./pages/user/Profile'))
const UserFollowersPage = lazy(() => import('./pages/user/Followers'))

//const UserPackCheckout = lazy(() => import ('./pages/user/PackCheckout'))
//const UserPackCheckoutSuccess = lazy(() => import ('./pages/user/PackCheckoutSuccess'))
//const UserPackCheckoutError = lazy(() => import ('./pages/user/PackCheckoutError'))

//const UserPackageCheckout = lazy(() => import ('./pages/user/PackageCheckout'))
const UserPackageCheckoutSuccess = lazy(() => import('./pages/checkout/PackageCheckoutSuccess.tsx'))

const UserNotificationsPage = lazy(() => import('./pages/user/Notifications'))
const UserUpcomingEventsPage = lazy(() => import('./pages/user/UpcomingEvents'))
const UserNotificationEventSwitchSuccess = lazy(() => import('./pages/user/NotificationEventSwitchSuccess'))
const UserSettingsPage = lazy(() => import('./pages/user/Settings'))

const UserBecomeHostPage = lazy(() => import('./pages/user/BecomeHost'))

const MembershipsCatalog = lazy(() => import('./pages/user/memberships/MembershipsCatalog'))
// Phase 4 Plan 04-02: Subscribe slice — PaymentElement checkout + polling return.
const MembershipPurchase = lazy(() => import('./pages/user/memberships/MembershipPurchase'))
const MembershipsReturn = lazy(() => import('./pages/user/memberships/MembershipsReturn'))
// Phase 4 Plan 04-03: Manage page — list user memberships + Stripe Billing Portal entry.
const MembershipsManage = lazy(() => import('./pages/user/memberships/MembershipsManage'))

// Phase 5 Plan 05-05: per-venue court × hour matrix (D-12).
const VenueCourts = lazy(() => import('./pages/user/VenueCourts'))
const VenueLessons = lazy(() => import('./pages/user/VenueLessons'))
const VenueMembershipsPage = lazy(() => import('./pages/user/VenueMemberships'))
// Phase 5 Plan 05-06: post-payment confirmation surface (D-13).
const CourtRentalSuccess = lazy(() => import('./pages/user/CourtRentalSuccess'))
// Phase 8 Plan 08-08 — Promoted-Hold Checkout (D-11).
const PromotedHoldCheckout = lazy(() => import('./pages/user/PromotedHoldCheckout'))

// Phase 6 Plan 06-07: player-facing lesson slot detail (PL-07).
const LessonSlotDetail = lazy(() => import('./pages/user/LessonSlotDetail'))
// Quick 260529-m1y — non-host coach availability page (reuses Phase6CoachAvailabilitySection).
const CoachAvailability = lazy(() => import('./pages/user/CoachAvailability'))
// Phase 8 Plan 08-07 — deep-link manage surface for waitlist entries.
const WaitlistManagePage = lazy(() => import('./pages/user/WaitlistManagePage'))

// Phase 7 Plan 07-08: post-payment confirmation surface for private lessons.
const LessonBookingSuccess = lazy(() => import('./pages/user/lesson/LessonBookingSuccess'))

const AdminAdminsPage = lazy(() => import('./pages/admin/Admins'))
const AdminVenuesPage = lazy(() => import('./pages/admin/Venues'))
const AdminEventsPage = lazy(() => import('./pages/admin/Events'))
const AdminPackagesPage = lazy(() => import('./pages/admin/Packages'))
const AdminNotificationsPage = lazy(() => import('./pages/admin/Notifications'))
const AdminPromotionsPage = lazy(() => import('./pages/admin/Promotions'))
const AdminHostsPage = lazy(() => import('./pages/admin/Hosts'))
const AdminUsersPage = lazy(() => import('./pages/admin/Users'))
const AdminAnalyticsPage = lazy(() => import('./pages/admin/Analytics'))
const AdminPurchasesPage = lazy(() => import('./pages/admin/Purchases'))
const AdminMigrationReviewPage = lazy(() => import('./pages/admin/MigrationReview'))
const AdminMigrationConflictDetailPage = lazy(() => import('./pages/admin/MigrationConflictDetail'))

// Phase 4 Plan 04-06: admin memberships CRUD + per-user grant/revoke (mounted under PrivateAdminRoute below).
const AdminMembershipsListPage = lazy(() => import('./pages/admin/memberships/AdminMembershipsList'))
const AdminMembershipSubscribersPage = lazy(() => import('./pages/admin/memberships/AdminMembershipSubscribers'))
const AdminUserMembershipGrantPage = lazy(() => import('./pages/admin/users/AdminUserMembershipGrant'))

const AdminVenueCalendarPage = lazy(() => import('./pages/admin/calendar/VenueCalendar'))
const AdminCalendarVenuePicker = lazy(() => import('./pages/admin/calendar/CalendarVenuePicker'))

const HostEventsPage = lazy(() => import('./pages/host/Events'))
const HostCreateEventPage = lazy(() => import('./pages/host/CreateEvent'))
const HostCreateEditLocationPage = lazy(() => import('./pages/host/CreateEditLocation'))
const HostCreateEventPageSuccess = lazy(() => import('./pages/host/CreateEventSuccess'))
const HostEditEventPage = lazy(() => import('./pages/host/EditEvent'))

import './App.css'

const PublicAuthRoute = ({ children }: { children: ReactNode }) => {
  const navigate = useNavigate()
  const { user, error } = useContext(AuthContext)

  useEffect(() => {
    if (user?.is_staff && !error) {
      navigate('/admin')
      return
    }
    if (user && !error) {
      const redirectUrl = localStorage.getItem('redirectAfterLogin')
      if (redirectUrl) {
        localStorage.removeItem('redirectAfterLogin')
        navigate(redirectUrl)
      } else {
        navigate('/user')
      }
    }
  }, [user, error])

  useEffect(() => {
    // Always disable WKWebView's built-in back/forward gesture so it
    // doesn't compete with our custom SwipeBack plugin (which provides
    // the iOS-native parallax transition for routes and drawers).
    setBackForwardNavigationGestures(false)
  }, [])

  return (
    <AuthWrapper>
      <Suspense fallback={<div />}>{children}</Suspense>
    </AuthWrapper>
  )
}

// Legacy `/venue/:id/courts` and `/venue/:id/lessons` URLs redirect to their
// new home under `/user/play/:venueId/*` so the venue 2-column layout persists.
const LegacyVenuePathRedirect = ({ to }: { to: 'courts' | 'lessons' }) => {
  const { id } = useParams<{ id: string }>()
  const { search } = useLocation()
  return <Navigate to={`/user/play/${id}/${to}${search}`} replace />
}

const PUBLIC_USER_PATHS = ['/user', '/user/play', '/user/shop', '/user/community']

// Pages an unauthenticated user may browse (discover venues/events, start a
// court booking). The actual booking/checkout still gates on login downstream.
const isPublicBrowsePath = (pathname: string): boolean => {
  if (PUBLIC_USER_PATHS.includes(pathname)) return true
  // Venue hub: /user/play/:id — but the /user/play/event booking page stays private.
  if (/^\/user\/play\/[^/]+$/.test(pathname) && pathname !== '/user/play/event') return true
  // Venue events list: /user/play/:venueId/events
  if (/^\/user\/play\/[^/]+\/events$/.test(pathname)) return true
  // Venue courts / lessons (book a court, lessons): /user/play/:venueId/courts|lessons
  if (/^\/user\/play\/[^/]+\/(courts|lessons)$/.test(pathname)) return true
  // Legacy book-a-court path (now redirects, but must be browseable so the redirect runs)
  if (/^\/venue\/[^/]+\/(courts|lessons)$/.test(pathname)) return true
  return false
}

const UserRoute = ({ children }: { children: ReactNode }) => {
  const navigate = useNavigate()
  const { user, error } = useContext(AuthContext)
  const location = useLocation()

  const isPublicPage = isPublicBrowsePath(location.pathname)

  useEffect(() => {
    setBackForwardNavigationGestures(false)
  }, [])

  useEffect(() => {
    if (user?.is_staff && !error) {
      const currentUrl = window.location.href
      const newUrl = currentUrl.replace('user', 'admin')
      window.location.href = newUrl
      return
    }

    // Only redirect to login for protected pages. Use replace so the private
    // page is swapped out of history — otherwise pressing back returns to it
    // and re-triggers this redirect, trapping the user in a login loop.
    if (!isPublicPage && (user == null || error)) {
      localStorage.setItem('redirectAfterLogin', location.pathname + location.search)
      navigate('/auth/login', { replace: true })
    }
  }, [user, error, location.pathname])

  return (
    <UserWrapper>
      <Suspense fallback={<div />}>{children}</Suspense>
    </UserWrapper>
  )
}

const PrivateHostRoute = ({ children }: { children: ReactNode }) => {
  const navigate = useNavigate()
  const { user, error, isHost } = useContext(AuthContext)

  useEffect(() => {
    setBackForwardNavigationGestures(false)
  }, [])

  // const patchRegistrationID = useMutation(["registration"], patchRegistration, {
  //   onError : () => {
  //     console.log("Error in sending registration token")
  //   }
  // })

  useEffect(() => {
    if (user?.is_staff && !error) navigate('/admin')
    if (user == null || error) navigate('/auth/login', { replace: true })
    // Check if user is a host - redirect non-hosts to user home
    if (user && !error && !isHost && !user?.member_profile?.is_host) {
      navigate('/user')
    }
  }, [user, error, isHost, navigate])

  return (
    <UserWrapper>
      <Suspense fallback={<div />}>{children}</Suspense>
    </UserWrapper>
  )
}

const PrivateCoachRoute = ({ children }: { children: ReactNode }) => {
  const navigate = useNavigate()
  const { user, error } = useContext(AuthContext)

  useEffect(() => {
    setBackForwardNavigationGestures(false)
  }, [])

  useEffect(() => {
    if (user?.is_staff && !error) navigate('/admin')
    if (user == null || error) navigate('/auth/login', { replace: true })
    // Check if user is a coach - redirect non-coaches to user home
    if (user && !error && !user?.member_profile?.is_coach) {
      navigate('/user')
    }
  }, [user, error, navigate])

  return (
    <UserWrapper>
      <Suspense fallback={<div />}>{children}</Suspense>
    </UserWrapper>
  )
}

const PublicCheckoutRoute = ({ children }: { children: ReactNode }) => {
  const navigate = useNavigate()
  const { user, error } = useContext(AuthContext)

  useEffect(() => {
    setBackForwardNavigationGestures(false)
  }, [])

  useEffect(() => {
    if (user?.is_staff && !error) navigate('/admin')
  }, [user, error])

  if (user != null) {
    return (
      <UserWrapper>
        <Suspense fallback={<div />}>{children}</Suspense>
      </UserWrapper>
    )
  }

  return (
    <>
      <Suspense fallback={<div />}>{children}</Suspense>
    </>
  )
}

const PrivateAdminRoute = ({ children }: { children: ReactNode }) => {
  const navigate = useNavigate()
  const { user, error } = useContext(AuthContext)

  useEffect(() => {
    if (!user?.is_staff && !error) navigate('/user')
    if (user == null || error) navigate('/auth/login', { replace: true })
  }, [user, error])

  return (
    <AdminWrapper>
      <Suspense fallback={<div />}>{children}</Suspense>
    </AdminWrapper>
  )
}

const Router = () => {
  const { loading } = useContext(AuthContext)
  const navigate = useNavigate()

  useEffect(() => {
    if (!Capacitor.isNativePlatform()) return

    const received = PushNotifications.addListener('pushNotificationReceived', (notification) => {
      console.log('Push notification received: ', notification)
      // Silent push that wakes the app to upload HealthKit samples for finished bookings.
      // Backend: event.tasks.dispatch_healthkit_sync_pushes.
      if (notification?.data?.type === 'healthkit_sync') {
        triggerHealthKitSync()
      }
    })
    const performed = PushNotifications.addListener('pushNotificationActionPerformed', (notification: ActionPerformed) => {
      console.log('Push notification action performed', notification.actionId, notification.inputValue)
      console.log(`Push notification: ${notification.notification.data.link}`)
      if (notification.notification.data.link) {
        navigate(notification.notification.data.link)
      }
    })
    return () => {
      void received.then((l) => l.remove())
      void performed.then((l) => l.remove())
    }
  }, [navigate])

  useEffect(() => {
    // Check if the URL contains the redirect_to parameter
    const currentURL = window.location.href

    if (currentURL.includes('redirect_to=')) {
      // Extract the value of the redirect_to parameter
      const url = new URL(currentURL)

      const redirectFragment = url.searchParams.get('redirect_to')
      url.search = url.searchParams.toString()
      const decodedFragment = decodeURI(redirectFragment ?? '')

      window.location.hash = decodedFragment
    }
  }, [])

  if (loading) return <LoadingPage />

  return (
    <Routes>
      <Route
        path="admin/*"
        element={
          <PrivateAdminRoute>
            <Routes>
              <Route path="/admin" element={<AdminAdminsPage />} />
              <Route path="/venues" element={<AdminVenuesPage />} />
              <Route path="/venues/create" element={<HostCreateEditLocationPage />} />
              <Route path="/venues/edit/:id" element={<HostCreateEditLocationPage isEdit />} />
              <Route path="/venues/:venueId/calendar" element={<AdminVenueCalendarPage />} />
              <Route path="/calendar" element={<AdminCalendarVenuePicker />} />
              <Route path="/events" element={<AdminEventsPage />} />
              <Route path="/events/create" element={<HostCreateEventPage />} />
              <Route path="/events/edit/:id" element={<HostEditEventPage />} />
              <Route path="/packages" element={<AdminPackagesPage />} />
              <Route path="/notifications" element={<AdminNotificationsPage />} />
              <Route path="/promotions" element={<AdminPromotionsPage />} />
              <Route path="/hosts" element={<AdminHostsPage />} />
              <Route path="" element={<AdminAnalyticsPage />} />

              <Route path="/users" element={<AdminUsersPage />} />
              <Route path="/purchases" element={<AdminPurchasesPage />} />
              <Route path="/migration" element={<AdminMigrationReviewPage />} />
              <Route path="/migration/:venueId" element={<AdminMigrationConflictDetailPage />} />

              {/* Phase 4 Plan 04-06: admin memberships routes (per UI-SPEC §3).
                  These are child paths matching the URL remainder after `admin/`
                  (e.g. `/memberships` matches `/admin/memberships`), consistent with
              the sibling routes above. */}
              <Route path="/memberships" element={<AdminMembershipsListPage />} />
              <Route path="/memberships/subscribers" element={<AdminMembershipSubscribersPage />} />
              <Route path="/users/:id/memberships" element={<AdminUserMembershipGrantPage />} />

              <Route path="/profile" element={<UserProfilePage />} />
              <Route path="/community" element={<UserCommunityPage />} />
            </Routes>
          </PrivateAdminRoute>
        }
      />

      <Route
        path="auth/*"
        element={
          <PublicAuthRoute>
            <Routes>
              <Route path="/login" element={<LoginPage />} />
              <Route path="/reset" element={<PasswordResetPage />} />
              <Route path="/register" element={<RegisterPage />} />
            </Routes>
          </PublicAuthRoute>
        }
      />

      <Route
        path="checkout/*"
        element={
          <PublicCheckoutRoute>
            <Routes>
              <Route path="/play" element={<EventCheckout />} />
              <Route path="/play/success" element={<EventSuccess />} />
              {/*<Route path="/play/error" element={<UserPlayCheckoutErrorPage />} />*/}

              <Route path="/book" element={<BookCheckout />} />
              <Route path="/book/success" element={<BookSuccess />} />
              {/*<Route path="/book/error" element={<UserPackCheckoutError />} />*/}

              <Route path="/pack" element={<PackCheckout />} />
              <Route path="/pack/success" element={<UserPackageCheckoutSuccess />} />
              {/*<Route path="/pack/error" element={<UserPackCheckoutError />} />*/}
            </Routes>
          </PublicCheckoutRoute>
        }
      />

      <Route
        path="user/play/:venueId/event/:eventId"
        element={
          <PublicCheckoutRoute>
            <UserEventSharePage />
          </PublicCheckoutRoute>
        }
      />

      <Route
        path="host/*"
        element={
          <PrivateHostRoute>
            <Routes>
              <Route path="/location/create" element={<HostCreateEditLocationPage />} />
              <Route path="/location/edit/:id" element={<HostCreateEditLocationPage isEdit />} />

              <Route path="/events" element={<HostEventsPage />} />
              <Route path="/event/create/success" element={<HostCreateEventPageSuccess />} />
              <Route path="/event/create" element={<HostCreateEventPage />} />
              <Route path="/event/edit/:id" element={<HostEditEventPage />} />
            </Routes>
          </PrivateHostRoute>
        }
      />

      <Route
        path="user/*"
        element={
          <UserRoute>
            <Routes>
              <Route path="/" element={<UserHomePage />} />
              <Route path="/play" element={<UserPlayPage />} />
              {/* <Route path="/play/checkout/success" element={<UserPlayCheckoutSuccessPage />} />
          <Route path="/play/checkout/error" element={<UserPlayCheckoutErrorPage />} />
          <Route path="/play/checkout/:id" element={<UserPlayCheckoutPage />} /> */}
              <Route path="/play/:venueId" element={<VenueLayout />}>
                <Route index element={<UserPlayExpandPage />} />
                <Route path="events" element={<VenuePlayEventsPage />} />
                <Route path="courts" element={<VenueCourts />} />
                <Route path="lessons" element={<VenueLessons />} />
                <Route path="memberships" element={<VenueMembershipsPage />} />
              </Route>
              <Route path="/play/event" element={<UserPlayEventPage />} />
              <Route path="/community" element={<UserCommunityPage />} />
              <Route path="/messages" element={<UserMessagesPage />} />
              <Route path="/shop" element={<UserProShopPage />} />
              <Route path="/profile" element={<UserProfilePage />} />
              <Route path="/followers" element={<UserFollowersPage />} />
              <Route path="/settings/:page" element={<UserSettingsPage />} />

              {/* <Route path='/pack/checkout/:id' element={<UserPackCheckout />} />
          <Route path='/pack/checkout/success' element={<UserPackCheckoutSuccess />} />
          <Route path='/pack/checkout/error' element={<UserPackCheckoutError />} /> */}

              {/* <Route path='/package/checkout' element={<UserPackageCheckout />} /> */}
              <Route path="/package/checkout/success" element={<UserPackageCheckoutSuccess />} />
              <Route path="/events" element={<UserUpcomingEventsPage />} />
              <Route path="/notifications" element={<UserNotificationsPage />} />
              <Route path="/notifications/switch/success" element={<UserNotificationEventSwitchSuccess />} />

              <Route path="/become-a-host" element={<Navigate to="/user/become-a-coach" replace />} />
              <Route path="/become-a-coach" element={<UserBecomeHostPage />} />
            </Routes>
          </UserRoute>
        }
      />
      <Route
        path="event/:eventId"
        element={
          <Suspense fallback={<div />}>
            <EventRedirect />
          </Suspense>
        }
      />

      {/* Phase 4 (04-01): Memberships catalog top-level route (HashRouter `#/memberships`).
          Native users redirect to `/memberships/mine` inside the page (MEM-07 / D-02). */}
      <Route
        path="memberships"
        element={
          <UserRoute>
            <MembershipsCatalog />
          </UserRoute>
        }
      />
      {/* Phase 4 (04-02): Subscribe slice — PaymentElement checkout (UserRoute).
          Page itself runs the native gate (T-04-02-06 / Apple 3.1.1). */}
      <Route
        path="memberships/checkout/:id"
        element={
          <UserRoute>
            <MembershipPurchase />
          </UserRoute>
        }
      />
      {/* Phase 4 (04-02): Stripe return landing (PublicCheckoutRoute — deep-link
          return without active session lands; page redirects to /memberships/mine
          which IS user-gated). Path differs from UI-SPEC §3 by planning_context
          decision: /memberships/checkout/:id wins over /memberships/:id/purchase. */}
      <Route
        path="memberships/return"
        element={
          <PublicCheckoutRoute>
            <MembershipsReturn />
          </PublicCheckoutRoute>
        }
      />
      {/* Phase 4 (04-03): Manage page — UserRoute on both web + native; the
          page itself branches on isNativePlatform() to render the
          Manage-on-web CTA without an in-app Billing Portal entry (D-02). */}
      <Route
        path="memberships/mine"
        element={
          <UserRoute>
            <MembershipsManage />
          </UserRoute>
        }
      />
      {/* Phase 5 Plan 05-05: per-venue court × hour booking matrix (D-12).
          Now nested under `/user/play/:venueId` so the desktop venue 2-column
          layout (VenueLayout) persists. Legacy paths redirect for any saved
          deep links. */}
      <Route path="venue/:id/courts" element={<LegacyVenuePathRedirect to="courts" />} />
      <Route path="venue/:id/lessons" element={<LegacyVenuePathRedirect to="lessons" />} />
      {/* Phase 5 Plan 05-06: court-rental confirmation surface (D-13).
          Registered under UserRoute so the redirect_to= unwrap in this Router
          (above) lands here after a post-payment login bounce (XC-01). */}
      <Route
        path="court-rental/:id/success"
        element={
          <UserRoute>
            <CourtRentalSuccess />
          </UserRoute>
        }
      />
      {/* Phase 6 Plan 06-07: lesson slot detail page (PL-07). Mounted under
          UserRoute so the existing redirect_to= deep-link unwrap (XC-01)
          lands here after login. */}
      <Route
        path="lesson/:slotId"
        element={
          <UserRoute>
            <LessonSlotDetail />
          </UserRoute>
        }
      />
      {/* Phase 7 Plan 07-08: lesson booking success page. UserRoute so the
          redirect_to= post-payment / post-login bounce (XC-01) lands here. */}
      <Route
        path="lesson/:slotId/booking-success"
        element={
          <UserRoute>
            <LessonBookingSuccess />
          </UserRoute>
        }
      />
      {/* Quick 260529-m1y — non-host coaches manage private-lesson availability
          inside the player shell. PrivateCoachRoute gates on member_profile.is_coach. */}
      <Route
        path="user/coach/availability"
        element={
          <PrivateCoachRoute>
            <CoachAvailability />
          </PrivateCoachRoute>
        }
      />
      <Route
        path="coach/availability"
        element={
          <PrivateCoachRoute>
            <CoachAvailability />
          </PrivateCoachRoute>
        }
      />
      {/* Phase 8 Plan 08-07 — deep-link manage surface for a waitlist
          entry. XC-01 HashRouter + redirect_to= deep-link plumbing
          reuses the existing AppUrlListener pattern (no listener
          changes). */}
      <Route
        path="/court-rentals/waitlist/:slotId"
        element={
          <UserRoute>
            <WaitlistManagePage />
          </UserRoute>
        }
      />
      {/* Phase 8 Plan 08-08 — Promoted-Hold Checkout (D-11). Target of the
          waitlist_promoted push notification deep link
          (#/private-lessons/promoted/<waitlist_id>). UserRoute so the
          existing redirect_to= post-login bounce (XC-01) lands here. */}
      <Route
        path="/private-lessons/promoted/:waitlistId"
        element={
          <UserRoute>
            <PromotedHoldCheckout />
          </UserRoute>
        }
      />
      <Route path="*" element={<IndexPage />} />
    </Routes>
  )
}

export const AuthContext = createContext<{
  user: User | null
  loading: boolean
  error: any
  isHost: boolean
  setIsHost: (v: boolean) => void
  logout: () => void
  signInWithGoogle: () => void
  signInWithFacebook: () => void
  signInWithApple: () => void
  signInWithEmailAndPassword: (email: string, password: string) => void
  registerWithEmailAndPassword: (email: string, password: string) => void
  getAccessToken: () => Promise<string>
  loginLoading: boolean
  loginError: boolean
  isAccountDeleted: boolean
  rememberMe: boolean
  setRememberMe: (v: boolean) => void
  userLocation?: { lat: number; lng: number } // Add this line
  setUserLocation: React.Dispatch<React.SetStateAction<{ lat: number; lng: number }>> // Add this line
}>({
  user: null,
  loading: true,
  error: null,
  isHost: false,
  setIsHost: () => {},
  logout: () => {},
  signInWithGoogle: () => {},
  signInWithFacebook: () => {},
  signInWithApple: () => {},
  signInWithEmailAndPassword: () => {},
  registerWithEmailAndPassword: () => {},
  getAccessToken: () => {
    return new Promise(() => '')
  },
  loginLoading: false,
  loginError: false,
  isAccountDeleted: false,
  rememberMe: false,
  setRememberMe: () => {},
  userLocation: { lat: 34.052235, lng: -118.243683 }, // Default location
  setUserLocation: () => {}, // Default no-op function
})

const App = () => {
  const { colorMode, toggleColorMode } = useColorMode()
  if (colorMode === 'dark') toggleColorMode()

  const {
    user,
    loading,
    error,
    logout,
    signInWithGoogle,
    signInWithFacebook,
    signInWithApple,
    signInWithEmailAndPassword,
    registerWithEmailAndPassword,
    getAccessToken,
    loginLoading,
    loginError,
    isAccountDeleted,
    rememberMe,
    setRememberMe,
    showForceUpdate,
  } = useAuthentication()

  const [userLocation, setUserLocation] = useState<{ lat: number; lng: number }>({
    lat: 34.052235, // Default center (Los Angeles)
    lng: -118.243683,
  })

  const initialHostSettings = async () => {
    const hostSettings = await localStorage.getItem('isHost')

    setIsHost(
      hostSettings && user?.member_profile?.is_host ? hostSettings == 'true' : user?.member_profile?.is_host ?? false
    )
  }

  useEffect(() => {
    initialHostSettings()
  }, [user])

  const [isHost, setIsHost] = useState<boolean>(user?.member_profile?.is_host ?? false)

  useEffect(() => {
    if (user != null) {
      console.log('CURRENT HOST SET UP: ', isHost)
      localStorage.setItem('isHost', JSON.stringify(isHost))
    }
  }, [isHost])

  const ScrollToTop = () => {
    const { pathname } = useLocation()

    useEffect(() => {
      window.scrollTo(0, 0)
      // Reset the swipe-back snapshot stack whenever the user lands on a
      // tab-bar root. Push sites only instrument forward nav; this keeps
      // the stack from drifting across tab switches or deep links.
      if (isIOS() && PUBLIC_USER_PATHS.includes(pathname)) {
        void SwipeBack.clearSnapshots()
      }
    }, [pathname])

    return null
  }

  return (
    <AuthContext.Provider
      value={{
        user,
        loading,
        error,
        isHost,
        setIsHost,
        logout,
        signInWithGoogle,
        signInWithFacebook,
        signInWithApple,
        signInWithEmailAndPassword,
        registerWithEmailAndPassword,
        getAccessToken,
        loginLoading,
        loginError,
        isAccountDeleted,
        rememberMe,
        setRememberMe,
        userLocation, // Provide userLocation
        setUserLocation, // Provide setUserLocation
      }}
    >
      <HashRouter>
        <ScrollToTop />
        <AppUrlListener />
        <Router />
        <ForceUpdateModal isOpen={showForceUpdate} />
      </HashRouter>
    </AuthContext.Provider>
  )
}

export default App
